/**
 * Visual Diff Service — browser-side screenshot capture and pixel comparison.
 *
 * Uses html-to-image for DOM capture and pixelmatch for pixel-level diff.
 */

import { toPng } from 'html-to-image'
import pixelmatch from 'pixelmatch'
import type { VisualDiffResult } from '../types'

/**
 * Capture an HTMLElement as a PNG data URL.
 */
export async function captureElement(el: HTMLElement): Promise<string> {
  return toPng(el, {
    quality: 1.0,
    pixelRatio: 1,
    cacheBust: true,
  })
}

/**
 * Load a data URL into an HTMLImageElement and return it after load.
 */
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = dataUrl
  })
}

/**
 * Compare two data URL images and produce a visual diff result.
 *
 * If dimensions differ, the larger image is scaled down to match the smaller
 * using canvas drawImage scaling.
 */
export async function compareImages(
  currentDataUrl: string,
  referenceDataUrl: string,
): Promise<VisualDiffResult> {
  const [currentImg, referenceImg] = await Promise.all([
    loadImage(currentDataUrl),
    loadImage(referenceDataUrl),
  ])

  // Use the smaller dimensions for comparison
  const width = Math.min(currentImg.width, referenceImg.width)
  const height = Math.min(currentImg.height, referenceImg.height)

  // Draw both images to canvases at the comparison size
  const currentCanvas = document.createElement('canvas')
  currentCanvas.width = width
  currentCanvas.height = height
  const currentCtx = currentCanvas.getContext('2d')!
  currentCtx.drawImage(currentImg, 0, 0, width, height)

  const referenceCanvas = document.createElement('canvas')
  referenceCanvas.width = width
  referenceCanvas.height = height
  const referenceCtx = referenceCanvas.getContext('2d')!
  referenceCtx.drawImage(referenceImg, 0, 0, width, height)

  // Extract pixel data
  const currentData = currentCtx.getImageData(0, 0, width, height)
  const referenceData = referenceCtx.getImageData(0, 0, width, height)

  // Create diff output
  const diffCanvas = document.createElement('canvas')
  diffCanvas.width = width
  diffCanvas.height = height
  const diffCtx = diffCanvas.getContext('2d')!
  const diffImageData = diffCtx.createImageData(width, height)

  // Run pixelmatch
  const mismatchCount = pixelmatch(
    currentData.data,
    referenceData.data,
    diffImageData.data,
    width,
    height,
    { threshold: 0.1 },
  )

  // Render diff to canvas
  diffCtx.putImageData(diffImageData, 0, 0)

  const totalPixels = width * height
  const diffPercent = totalPixels > 0
    ? Math.round((mismatchCount / totalPixels) * 10000) / 100
    : 0

  return {
    diffPercent,
    diffImageDataUrl: diffCanvas.toDataURL('image/png'),
    currentImageDataUrl: currentCanvas.toDataURL('image/png'),
    referenceImageDataUrl: referenceCanvas.toDataURL('image/png'),
    width,
    height,
  }
}

/**
 * Convenience class wrapping capture + compare.
 */
export class VisualDiffService {
  async capture(el: HTMLElement): Promise<string> {
    return captureElement(el)
  }

  async compare(currentDataUrl: string, referenceDataUrl: string): Promise<VisualDiffResult> {
    return compareImages(currentDataUrl, referenceDataUrl)
  }

  async captureAndCompare(el: HTMLElement, referenceDataUrl: string): Promise<VisualDiffResult> {
    const currentDataUrl = await this.capture(el)
    return this.compare(currentDataUrl, referenceDataUrl)
  }
}
